import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, QrCode, Search, X } from 'lucide-react';
import type { Tool } from '@shared/types/inventory';
import type { Technician } from '@shared/types';
import { getApiUrl } from '@/config';
import toast from 'react-hot-toast';
import jsQR from 'jsqr';
import { Html5Scanner } from './Scanner/Html5Scanner';

interface TechnicianInventoryViewProps {
    myTechnician: Technician;
    tools: Tool[];
    onUpdateTools: (tools: Tool[]) => void;
}

export const TechnicianInventoryView: React.FC<TechnicianInventoryViewProps> = ({
    myTechnician,
    tools,
    onUpdateTools
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isScanning, setIsScanning] = useState(false);

    const myTools = useMemo(() => {
        return tools.filter(t => t.assignedTo === myTechnician.id);
    }, [tools, myTechnician.id]);

    const filteredTools = useMemo(() => {
        return myTools.filter(t =>
            t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.serialNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [myTools, searchTerm]);

    const handleScan = (result: string) => {
        if (result) {
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
                navigator.vibrate(100);
            }
            setIsScanning(false);
            assignToolByQR(result);
        }
    };

    const assignToolByQR = async (qrValue: string) => {
        const scanVal = qrValue.toLowerCase().trim();
        const tool = tools.find(t =>
            (t.qrIdentifier && t.qrIdentifier.toLowerCase() === scanVal) ||
            t.id.toLowerCase() === scanVal ||
            (t.serialNumber && t.serialNumber.toLowerCase() === scanVal)
        );

        if (tool) {
            if (tool.assignedTo === myTechnician.id) {
                toast.error(`Cet outil (${tool.name}) est déjà en ma possession.`);
            } else if (tool.status === 'assigned') {
                toast.error(`Cet outil (${tool.name}) est déjà assigné à quelqu'un d'autre.`);
            } else if (tool.status === 'maintenance' || tool.status === 'lost') {
                toast.error(`Cet outil (${tool.name}) n'est pas disponible (Statut: ${tool.status}).`);
            } else {
                if (window.confirm(`Voulez-vous ajouter "${tool.name}" à votre inventaire ?`)) {
                    try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(getApiUrl(`/api/tools/${tool.id}/assign`), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                technicianId: myTechnician.id,
                                technicianName: myTechnician.name
                            })
                        });

                        if (res.ok) {
                            const data = await res.json();
                            const updatedTools = tools.map(t =>
                                t.id === tool.id ? data.tool : t
                            );
                            onUpdateTools(updatedTools);
                            toast.success(`"${tool.name}" a été ajouté à votre inventaire.`);
                        } else {
                            toast.error("Erreur lors de l'attribution.");
                        }
                    } catch (e) {
                        console.error(e);
                        toast.error("Erreur réseau lors de l'attribution.");
                    }
                }
            }
        } else {
            toast.error("Aucun outil trouvé avec ce code QR.");
        }
    };

    const handleFileCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        toast.loading("Analyse de la photo...", { id: 'qr-scan' });

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;

                    const MAX_SIZE = 800;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_SIZE) {
                            height *= MAX_SIZE / width;
                            width = MAX_SIZE;
                        }
                    } else {
                        if (height > MAX_SIZE) {
                            width *= MAX_SIZE / height;
                            height = MAX_SIZE;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const code = jsQR(imageData.data, imageData.width, imageData.height);

                    if (code) {
                        toast.success("Code détecté !", { id: 'qr-scan' });
                        if (typeof navigator !== 'undefined' && navigator.vibrate) {
                            navigator.vibrate(100);
                        }
                        setIsScanning(false);
                        assignToolByQR(code.data);
                    } else {
                        toast.error("Aucun QR détecté sur la photo.", { id: 'qr-scan' });
                    }
                } catch (err) {
                    toast.error("Erreur d'analyse.", { id: 'qr-scan' });
                } finally {
                    e.target.value = '';
                }
            };
            img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="space-y-6 pb-24">
            <input
                id="manual-qr-input"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileCapture}
            />

            <div className="flex flex-col gap-4">
                <button
                    onClick={() => setIsScanning(true)}
                    className="w-full py-4 bg-primary/20 border border-primary/50 text-white rounded-xl flex items-center justify-center gap-3 font-semibold shadow-lg shadow-primary/10 active:scale-95 transition-all"
                >
                    <QrCode size={24} />
                    <span>Scanner un outil</span>
                </button>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="glass-input w-full pl-10 py-3"
                    />
                </div>
            </div>

            <div className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Wrench size={16} />
                    Mon Inventaire ({filteredTools.length})
                </h2>

                <div className="grid grid-cols-1 gap-3">
                    {filteredTools.map(tool => (
                        <div key={tool.id} className="glass-panel p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-glass-border/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {tool.image ? (
                                    <img src={tool.image} alt={tool.name} className="w-full h-full object-cover" />
                                ) : (
                                    <Wrench className="text-muted" size={20} />
                                )}
                            </div>
                            <div className="flex-grow min-w-0">
                                <h3 className="font-bold text-gray-200 truncate">{tool.name}</h3>
                                <p className="text-xs text-gray-400">{tool.category} • {tool.serialNumber || 'S/N absent'}</p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <div className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                                    Acquis
                                </div>
                                <button
                                    onClick={async () => {
                                        if (window.confirm(`Voulez-vous remettre "${tool.name}" au stock ?`)) {
                                            try {
                                                const token = localStorage.getItem('token');
                                                const res = await fetch(getApiUrl(`/api/tools/${tool.id}/return`), {
                                                    method: 'POST',
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                                if (res.ok) {
                                                    const data = await res.json();
                                                    const updatedTools = tools.map(t =>
                                                        t.id === tool.id ? data.tool : t
                                                    );
                                                    onUpdateTools(updatedTools);
                                                    toast.success(`"${tool.name}" a été remis au stock.`);
                                                } else {
                                                    toast.error("Erreur lors de la restitution.");
                                                }
                                            } catch (e) {
                                                toast.error("Erreur réseau.");
                                            }
                                        }
                                    }}
                                    className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 text-gray-400 border border-white/10 transition-colors"
                                >
                                    Restituer
                                </button>
                            </div>
                        </div>
                    ))}
                    {filteredTools.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            <p>Aucun outil.</p>
                        </div>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {isScanning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black flex flex-col"
                    >
                        <div className="p-4 flex items-center justify-between bg-black/50 backdrop-blur-sm absolute top-0 left-0 right-0 z-10 w-full">
                            <h3 className="text-white font-bold">Scanner</h3>
                            <button onClick={() => setIsScanning(false)} className="p-2 bg-white/10 rounded-full">
                                <X size={24} className="text-white" />
                            </button>
                        </div>

                        <div className="flex-1 relative bg-black overflow-hidden">
                            <Html5Scanner onScan={handleScan} isScanning={isScanning} />
                        </div>

                        <div className="p-8 pb-12 text-center bg-black/80 backdrop-blur-sm absolute bottom-0 left-0 right-0 w-full flex flex-col gap-4">
                            <p className="text-white/70 text-sm">Le scan est automatique</p>
                            <button
                                onClick={() => document.getElementById('manual-qr-input')?.click()}
                                className="w-full py-4 bg-white/10 text-white border border-white/20 font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all text-sm"
                            >
                                <QrCode size={18} />
                                Utiliser l'appareil photo si besoin
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
